import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV } from "../../data/content";
import { useLang } from "../../i18n/LanguageContext";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
    <span className="relative flex h-8 w-8 items-center justify-center">
      <span className="absolute inset-0 rounded-full bg-[#3B82F6] blur-md opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
      <span className="relative h-3.5 w-3.5 rounded-full bg-gradient-to-tr from-[#3B82F6] to-[#F59E0B]" />
    </span>
    <span className="text-lg font-semibold tracking-tight text-white">Jorviko</span>
  </Link>
);

const LangToggle = () => {
  const { lang, toggle } = useLang();
  return (
    <button
      onClick={toggle}
      data-testid="lang-toggle"
      className="font-mono text-xs tracking-widest text-white/70 hover:text-white transition-colors duration-300 border border-white/10 rounded-full px-3 py-1.5 hover:border-white/30"
      aria-label="Toggle language"
    >
      {lang === "en" ? "EN" : "NO"} <span className="text-white/30">/</span>{" "}
      <span className="text-white/40">{lang === "en" ? "NO" : "EN"}</span>
    </button>
  );
};

const NavItem = ({ item, active }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  if (!item.children) {
    return (
      <Link
        to={item.to}
        data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
        className={`relative px-4 py-2 text-sm transition-colors duration-300 ${active ? "text-white" : "text-white/60 hover:text-white"}`}
      >
        {t(item.label)}
        {active && <motion.span layoutId="nav-dot" className="absolute left-1/2 -bottom-0.5 h-1 w-1 -translate-x-1/2 rounded-full bg-[#60A5FA]" />}
      </Link>
    );
  }

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link
        to={item.to}
        data-testid={`nav-${item.to.slice(1)}`}
        className={`relative flex items-center gap-1 px-4 py-2 text-sm transition-colors duration-300 ${active ? "text-white" : "text-white/60 hover:text-white"}`}
      >
        {t(item.label)}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        {active && <motion.span layoutId="nav-dot" className="absolute left-1/2 -bottom-0.5 h-1 w-1 -translate-x-1/2 rounded-full bg-[#60A5FA]" />}
      </Link>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            data-testid="nav-services-dropdown"
            className="absolute left-0 top-full pt-3 w-64"
          >
            <div className="rounded-2xl border border-white/10 bg-[#0A0E17]/95 backdrop-blur-xl p-2 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              {item.children.map((child) => (
                <Link
                  key={child.to}
                  to={child.to}
                  data-testid={`nav-${child.to.split("/").pop()}`}
                  className="block rounded-xl px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors duration-200"
                >
                  {t(child.label)}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const isActive = (item) => location.pathname === item.to || (item.children?.some((c) => c.to === location.pathname) ?? false);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#02060D]/70 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavItem key={item.to} item={item} active={isActive(item)} />
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle />
            <Link
              to="/contact"
              data-testid="header-cta"
              className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-[#3B82F6]/15 border border-[#3B82F6]/40 text-[#93c5fd] px-4 py-2 text-sm hover:bg-[#3B82F6] hover:text-white transition-all duration-300 shadow-[0_0_20px_rgba(59,130,246,0.25)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]"
            >
              {t({ en: "Discuss Your Project", no: "Diskuter ditt prosjekt" })}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-[#02060D]/95 backdrop-blur-2xl pt-24 px-6 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-2 pb-12">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="border-b border-white/5"
                >
                  {item.children ? (
                    <>
                      <div className="flex items-center justify-between py-3">
                        <Link to={item.to} data-testid={`mobile-nav-${item.to.slice(1)}`} className="text-3xl font-medium tracking-tight text-white">
                          {t(item.label)}
                        </Link>
                        <button
                          onClick={() => setMobileServicesOpen((v) => !v)}
                          aria-label="Toggle services"
                          data-testid="mobile-services-toggle"
                          className="p-2 text-white/60"
                        >
                          <ChevronDown className={`h-6 w-6 transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                      <AnimatePresence>
                        {mobileServicesOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-4 pb-3"
                          >
                            {item.children.map((child) => (
                              <Link
                                key={child.to}
                                to={child.to}
                                data-testid={`mobile-nav-${child.to.split("/").pop()}`}
                                className="block py-2.5 text-lg text-white/60 hover:text-white"
                              >
                                {t(child.label)}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                      className="block py-3 text-3xl font-medium tracking-tight text-white"
                    >
                      {t(item.label)}
                    </Link>
                  )}
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
